/**
 * ConfirmDialog.tsx — Modal confirmation dialog
 *
 * Purpose:
 *   Presents a blocking confirmation prompt before destructive admin actions
 *   (e.g., cancelling subscriptions, revoking operator access). Renders a
 *   simple overlay + dialog box with Confirm and Cancel buttons.
 *
 * Public API:
 *   - ConfirmDialog  Controlled modal dialog component
 *
 * @module packages/billing-admin/src/components/ConfirmDialog
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React from 'react';

interface ConfirmDialogProps {
  /** Whether the dialog is currently visible. */
  open: boolean;
  /** Dialog title shown in the header. */
  title: string;
  /** Detailed message asking the user to confirm the action. */
  message: React.ReactNode;
  /** Label for the confirm button (default: 'Confirm'). */
  confirmLabel?: string;
  /** Label for the cancel button (default: 'Cancel'). */
  cancelLabel?: string;
  /** Visual variant for the confirm button. */
  variant?: 'default' | 'danger';
  /** Called when the user clicks the confirm button. */
  onConfirm: () => void;
  /** Called when the user clicks cancel or the backdrop. */
  onCancel: () => void;
  /** Whether the confirm action is in progress (disables buttons). */
  loading?: boolean;
}

/**
 * ConfirmDialog — renders a modal confirmation prompt.
 *
 * EDGE CASE: Returns null when open=false to avoid rendering an invisible
 * overlay in the DOM.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmDialogProps): React.JSX.Element | null {
  if (!open) return null;

  const confirmBtnClass = variant === 'danger'
    ? 'bg-red-600 text-white hover:bg-red-700'
    : 'bg-cyan-600 text-white hover:bg-cyan-700';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      {/* Dialog box — stopPropagation prevents backdrop click from closing */}
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900">
          {title}
        </h2>
        <div className="mt-2 text-sm text-gray-600">{message}</div>
        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-md disabled:opacity-50 ${confirmBtnClass}`}
          >
            {loading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
